// Validation d'une écriture dans un outil de l'entreprise (GLPI, CISO Assistant, Mercator), constats O3 et O39.
//
// En deux temps (O95). L'application n'envoie jamais de plan : (1) elle envoie la DEMANDE (logiciel, table, demande) à
// POST /plans ; l'API fait planifier le collecteur du client, authentifie le plan, le GARDE pour cette personne et le
// rend avec son empreinte et son échéance, sans rien signer ; (2) la personne le lit, le confirme, et
// POST /validations désigne ce plan gardé (plan_id + empreinte vue) : l'API signe alors CE plan, à usage unique, et
// renvoie {plan, validation}. Le plan peut différer de ce que la personne attendait : elle le lit avant toute validation. L'application remet plan et validation tels quels, sans les modifier. La
// validation n'est gardée qu'en mémoire, le temps de la remettre au connecteur ; jamais dans localStorage ni dans un
// cache. L'application ne détient aucune clé : elle ne peut pas fabriquer une validation.
//
// Le plan est normalisé (champs par défaut écrits, champs inconnus refusés) pour que l'empreinte calculée ici, sur
// le plan affiché, soit celle que l'API signe : JSON canonique RFC 8785 puis SHA-256, comme ekera.connecteurs.
import type { components } from '../api/schema';

type S = components['schemas'];
export type PlanEcriture = S['PlanEcriture'];
export type ActionPlan = S['Action'];
export type DemandeEcriturePlan = S['DemandeEcriture'];
export type Validation = S['Validation'];
export type DemandeValidation = S['DemandeValidation'];
export type ReponseValidation = S['ReponseValidation'];
export type ReponsePlan = S['ReponsePlan'];

/** Durée de validité annoncée par l'API (§ 8.10 de PIVOT-API.md). */
export const DUREE_VALIDATION_MS = 15 * 60 * 1000;

export class PlanIllisible extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PlanIllisible';
  }
}

const CHAMPS_PLAN = ['logiciel', 'table', 'demande', 'actions', 'versions_lues'] as const;
const CHAMPS_DEMANDE = ['nature', 'cle', 'contenu', 'cible', 'version_lue'] as const;
const CHAMPS_ACTION = ['nature', 'type_logiciel', 'identifiant', 'donnees', 'motif'] as const;

function objet(v: unknown, ou: string): Record<string, unknown> {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) throw new PlanIllisible(`${ou} : objet attendu`);
  return v as Record<string, unknown>;
}

function seulement(o: Record<string, unknown>, champs: readonly string[], ou: string): void {
  const inconnus = Object.keys(o).filter((k) => !champs.includes(k));
  if (inconnus.length)
    throw new PlanIllisible(`${ou} : champ inconnu (${inconnus.join(', ')}) ; un plan modifié à la main n’est pas validé`);
}

function texte(v: unknown, ou: string): string {
  if (typeof v !== 'string' || !v.trim()) throw new PlanIllisible(`${ou} : texte attendu`);
  return v;
}

function texteOuNul(v: unknown, ou: string): string | null {
  if (v === undefined || v === null) return null;
  if (typeof v !== 'string') throw new PlanIllisible(`${ou} : texte attendu`);
  return v;
}

// Un entier au-delà de 2^53 serait arrondi par JavaScript : l'empreinte calculée ici différerait alors de celle que
// l'API signe, et l'application refuserait la validation (comparaison des empreintes) ; rien ne passerait en silence.
function verifierNombres(v: unknown, ou: string): void {
  if (typeof v === 'number' && !Number.isFinite(v)) throw new PlanIllisible(`${ou} : nombre invalide`);
  if (Array.isArray(v)) v.forEach((x, i) => verifierNombres(x, `${ou}[${i}]`));
  else if (typeof v === 'object' && v !== null) for (const [k, x] of Object.entries(v)) verifierNombres(x, `${ou}.${k}`);
}

/**
 * Lit le plan rendu par la simulation d'un connecteur (fichier JSON : le plan seul, ou `{"plan": …}`) et le rend
 * sous sa forme complète, celle que l'API relit et signe. Refuse un plan incomplet ou augmenté de champs inconnus.
 */
export function lirePlan(brut: string | unknown): PlanEcriture {
  let v: unknown = brut;
  if (typeof brut === 'string') {
    try {
      v = JSON.parse(brut);
    } catch {
      throw new PlanIllisible('fichier illisible : JSON attendu');
    }
  }
  let p = objet(v, 'plan');
  if ('plan' in p && Object.keys(p).length === 1) p = objet(p.plan, 'plan');
  seulement(p, CHAMPS_PLAN, 'plan');
  const d = objet(p.demande, 'plan.demande');
  seulement(d, CHAMPS_DEMANDE, 'plan.demande');
  if (!Array.isArray(p.actions)) throw new PlanIllisible('plan.actions : liste attendue');
  const versions = objet(p.versions_lues ?? {}, 'plan.versions_lues');
  const plan: PlanEcriture = {
    logiciel: texte(p.logiciel, 'plan.logiciel'),
    table: texte(p.table, 'plan.table'),
    demande: {
      nature: texte(d.nature, 'plan.demande.nature'),
      cle: texte(d.cle, 'plan.demande.cle'),
      contenu: objet(d.contenu ?? {}, 'plan.demande.contenu'),
      cible: texteOuNul(d.cible, 'plan.demande.cible'),
      version_lue: texteOuNul(d.version_lue, 'plan.demande.version_lue'),
    },
    actions: p.actions.map((x, i): ActionPlan => {
      const a = objet(x, `plan.actions[${i}]`);
      seulement(a, CHAMPS_ACTION, `plan.actions[${i}]`);
      return {
        nature: texte(a.nature, `plan.actions[${i}].nature`),
        type_logiciel: texte(a.type_logiciel, `plan.actions[${i}].type_logiciel`),
        identifiant: texteOuNul(a.identifiant, `plan.actions[${i}].identifiant`),
        donnees: objet(a.donnees ?? {}, `plan.actions[${i}].donnees`),
        motif: texte(a.motif, `plan.actions[${i}].motif`),
      };
    }),
    versions_lues: Object.fromEntries(
      Object.entries(versions).map(([k, x]) => [k, texteOuNul(x, `plan.versions_lues.${k}`)]),
    ),
  };
  verifierNombres(plan, 'plan');
  return plan;
}

export interface DemandeLue {
  logiciel: string;
  table: string;
  demande: DemandeEcriturePlan;
  /** Empreinte du plan que contenait le fichier, s'il en contenait un (ancien fichier de simulation) ; sinon null. */
  empreinteAttendue: string | null;
}

/**
 * Lit le fichier de la demande : `{logiciel, table, demande}`. Un ancien fichier de plan est accepté, mais seuls
 * `logiciel`, `table` et `demande` sont envoyés : les actions du fichier ne sont jamais transmises (l'API fait
 * planifier le collecteur), elles servent seulement à signaler si le plan recalculé en diffère.
 */
export async function lireDemande(brut: string): Promise<DemandeLue> {
  let v: unknown;
  try {
    v = JSON.parse(brut);
  } catch {
    throw new PlanIllisible('fichier illisible : JSON attendu');
  }
  let p = objet(v, 'demande');
  if ('plan' in p && Object.keys(p).length === 1) p = objet(p.plan, 'demande');
  const avecPlan = 'actions' in p;
  if (!avecPlan) seulement(p, ['logiciel', 'table', 'demande'], 'demande');
  const plan = lirePlan({
    logiciel: p.logiciel,
    table: p.table,
    demande: p.demande,
    actions: avecPlan ? p.actions : [],
    ...(avecPlan && 'versions_lues' in p ? { versions_lues: p.versions_lues } : {}),
  });
  return {
    logiciel: plan.logiciel,
    table: plan.table,
    demande: plan.demande,
    empreinteAttendue: avecPlan ? await empreintePlan(plan) : null,
  };
}

/** Écarts entre le plan rendu par l'API et la demande envoyée ; vide si le plan porte bien sur cette demande. */
export function ecartsPlan(plan: PlanEcriture, envoyee: Pick<DemandeLue, 'logiciel' | 'table' | 'demande'>): string[] {
  const e: string[] = [];
  if (plan.logiciel !== envoyee.logiciel) e.push('le plan porte sur un autre logiciel que celui demandé');
  if (plan.table !== envoyee.table) e.push('le plan porte sur une autre table de correspondance que celle demandée');
  if (jsonCanonique(plan.demande) !== jsonCanonique(envoyee.demande)) e.push('le plan porte sur une autre demande que celle envoyée');
  return e;
}

/**
 * JSON canonique RFC 8785 (JCS) : clés triées par unités UTF-16, sans espace ; nombres et chaînes sérialisés comme
 * ECMAScript (JSON.stringify), ce que la RFC reprend. Même résultat que la bibliothèque `rfc8785` de l'API.
 */
export function jsonCanonique(v: unknown): string {
  if (v === null || typeof v !== 'object') {
    if (typeof v === 'number' && !Number.isFinite(v)) throw new PlanIllisible('nombre invalide');
    return JSON.stringify(v);
  }
  if (Array.isArray(v)) return `[${v.map(jsonCanonique).join(',')}]`;
  const cles = Object.keys(v)
    .filter((k) => (v as Record<string, unknown>)[k] !== undefined)
    .sort();
  return `{${cles.map((k) => `${JSON.stringify(k)}:${jsonCanonique((v as Record<string, unknown>)[k])}`).join(',')}}`;
}

/** SHA-256 (hexadécimal) du plan en JSON canonique : l'empreinte que la validation doit porter. */
export async function empreintePlan(plan: PlanEcriture): Promise<string> {
  const octets = new TextEncoder().encode(jsonCanonique(plan));
  const h = await crypto.subtle.digest('SHA-256', octets);
  return [...new Uint8Array(h)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Nombre d'actions par nature (« creer : 3, modifier : 2 »), « aucune » comprise. */
export function resumePlan(plan: PlanEcriture): { nature: string; nombre: number }[] {
  const n = new Map<string, number>();
  for (const a of plan.actions) n.set(a.nature, (n.get(a.nature) ?? 0) + 1);
  return [...n.entries()].sort(([a], [b]) => a.localeCompare(b, 'fr')).map(([nature, nombre]) => ({ nature, nombre }));
}

export const LIBELLE_ACTION: Record<string, string> = {
  creer: 'créer',
  modifier: 'modifier',
  ajouter_suivi: 'ajouter un suivi',
  annuler: 'marquer annulé',
  restaurer: 'restaurer la valeur d’avant',
  signaler: 'signaler au responsable',
  aucune: 'rien à faire (déjà fait)',
};

export const LIBELLE_LOGICIEL: Record<string, string> = {
  glpi: 'GLPI',
  ciso_assistant: 'CISO Assistant',
  mercator: 'Mercator',
};

/** Millisecondes restantes avant l'échéance de la validation (0 si elle est passée ou illisible). */
export function tempsRestant(v: Pick<Validation, 'expire'>, maintenant: Date): number {
  const fin = v.expire ? Date.parse(v.expire) : Number.NaN;
  return Number.isNaN(fin) ? 0 : Math.max(0, fin - maintenant.getTime());
}

/** « 14 min 05 s » ; « 45 s ». */
export function dureeLisible(ms: number): string {
  const s = Math.ceil(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m} min ${String(r).padStart(2, '0')} s` : `${r} s`;
}

/**
 * Ce que l'application contrôle sur le plan gardé par l'API avant de proposer de le valider : l'empreinte recalculée
 * ici est celle que l'API annonce, le plan porte sur la demande envoyée, l'échéance est lisible. Vide si tout va bien.
 */
export function ecartsPlanGarde(
  recu: Pick<ReponsePlan, 'plan' | 'empreinte' | 'expire'>,
  empreinteLocale: string,
  envoyee: Pick<DemandeLue, 'logiciel' | 'table' | 'demande'>,
): string[] {
  const e = ecartsPlan(recu.plan, envoyee);
  if (empreinteLocale !== recu.empreinte) e.push('l’empreinte du plan recalculée dans votre navigateur ne correspond pas à celle annoncée par l’API');
  if (Number.isNaN(Date.parse(recu.expire))) e.push('échéance du plan illisible');
  return e;
}

/** Le plan rendu par la validation est celui qui a été affiché (octet pour octet, en JSON canonique). */
export function memePlan(a: PlanEcriture, b: PlanEcriture): boolean {
  return jsonCanonique(a) === jsonCanonique(b);
}

/** Ce que la validation doit prouver avant d'être remise au connecteur ; vide si tout va bien. */
export function ecartsValidation(v: Validation, plan: { empreinte: string }, carte: string, sujet?: string): string[] {
  const e: string[] = [];
  if (!v.signature || !v.jeton || !v.expire || !v.identite) e.push('validation non signée par l’API');
  if (v.empreinte_plan !== plan.empreinte) e.push('l’API a validé un autre plan que celui affiché');
  if (v.carte && v.carte !== carte) e.push(`validation émise pour une autre carte (${v.carte})`);
  if (sujet && v.identite?.sujet && v.identite.sujet !== sujet) e.push('validation émise pour une autre personne');
  return e;
}

/** Le fichier remis au connecteur : le plan exact et sa validation signée (sans rien d'autre). */
export function planValide(plan: PlanEcriture, v: Validation, attestation: Record<string, unknown> | null = null): { nom: string; contenu: string } {
  // l'attestation de fin de circuit accompagne la validation : le connecteur la vérifie lui-même (CV-2, CV-3)
  return {
    nom: `plan-valide-${(v.jeton ?? 'sans-jeton').replace(/[^A-Za-z0-9_-]/g, '')}.json`,
    contenu: `${JSON.stringify(attestation ? { plan, validation: v, attestation } : { plan, validation: v }, null, 1)}\n`,
  };
}
